import MicroModal from 'micromodal';
import ParameterValidator from '@mvps-genesis/parameter-validator';

/*
 *--------------------------------------------------------------------------
 * WishlistDialog
 *--------------------------------------------------------------------------
 *
 * Functionality for the wishlist dialog element.
 *
 */
export default class WishlistDialog {

	/**
	 * Parameter Validator class object
	 *
	 * @var	{ParameterValidator} @mvps-genesis/parameter-validator
	 */
	#validator;

	/**
	 * The wishlist dialog root container element
	 *
	 * @var {HTMLElement}
	 */
	dialogElement;

	/**
	 * The wishlist dialog container element, typically the first child of the dialog root container element
	 *
	 * @var {HTMLElement}
	 */
	dialogContainerElement;

	/**
	 * The control element (aria-controls) associated to the dialog root container element
	 *
	 * @var {HTMLElement}
	 */
	controlElement;

	/**
	 * Toggle functionality options and elements
	 *
	 * @var {Object}
	 */
	toggle;

	/**
	 * URL parameter name to trigger displaying the wishlist dialog on page load
	 *
	 * @var {String}
	 */
	showDialogParameter;

	/**
	 * Create a new WishlistDialog object.
	 *
	 * @param  {HTMLElement|String}  dialogElement                            The wishlist dialog root container element.
	 * @param  {HTMLElement|String}  controlElement                           The control element (aria-controls) associated to the dialog root container element.
	 * @param  {HTMLElement|String}  options.dialogContainerElement           The wishlist dialog container element, typically the first child of the dialog root container element. Defaults to '.g-add-to-wishlist-dialog__container'.
	 * @param  {Boolean}             options.dialogScrollEnabled              Flag to enable or disable the dialog scroll into view functionality. Defaults to true.
	 * @param  {Object}              options.dialogScrollIntoView             Scroll into view options for the dialog when the showDialogParameter is present in the URL. This object should correlate to the scrollIntoViewOptions object defined here - https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollIntoView. Defaults to behavior = smooth, block = center.
	 * @param  {Boolean}             options.toggleEnabled                    Flag to enable / disable the toggle functionality. Defaults to true.
	 * @param  {String}              options.toggleOpenClass                  The toggle is open class name. Defaults to 'g-add-to-wishlist-dialog__toggle--open'.
	 * @param  {String}              options.togglePrimaryControlElementId    The id attribute on the primary toggle control element. Defaults to 'js-wishlists-primary-toggle-control'.
	 * @param  {String}              options.toggleSecondaryControlElementId  The id attribute on the secondary toggle control element. Defaults to 'js-wishlists-secondary-toggle-control'.
	 * @param  {String}              options.showDialogParameter              URL parameter name to trigger displaying the wishlist dialog on page load. Defaults to 'show_wishlist'.
	 */
	constructor (dialogElement, controlElement, {
		dialogContainerElement = '.g-add-to-wishlist-dialog__container',
		dialogScrollEnabled = true,
		dialogScrollIntoView = {
			behavior: 'smooth',
			block: 'center'
		},
		toggleEnabled = true,
		toggleOpenClass = 'g-add-to-wishlist-dialog__toggle--open',
		togglePrimaryControlElementId = 'js-wishlists-primary-toggle-control',
		toggleSecondaryControlElementId = 'js-wishlists-secondary-toggle-control',
		showDialogParameter = 'show_wishlist'
	} = {}) {
		this.#validator = new ParameterValidator(this);

		this.dialogElement = this.#validator.isElement('dialogElement', dialogElement);
		this.dialogContainerElement = this.#validator.isElement('dialogContainerElement', dialogContainerElement, {container: this.dialogElement});
		this.controlElement = this.#validator.isElement('controlElement', controlElement);

		this.toggle = {
			enabled: this.#validator.isBoolean('toggleEnabled', toggleEnabled)
		};

		if (this.toggle.enabled) {
			this.toggle.elements = {
				primary: {
					controlElement: this.#validator.isElement('togglePrimaryControlElementId', document.getElementById(togglePrimaryControlElementId))
				},
				secondary: {
					controlElement: this.#validator.isElement('toggleSecondaryControlElementId', document.getElementById(toggleSecondaryControlElementId))
				}
			};

			this.toggle.elements.primary.element = this.#validator.isElement('toggle.elements.primary.element', document.getElementById(this.toggle.elements.primary.controlElement.getAttribute('aria-controls')));
			this.toggle.elements.secondary.element = this.#validator.isElement('toggle.elements.secondary.element', document.getElementById(this.toggle.elements.secondary.controlElement.getAttribute('aria-controls')));
			this.toggle.openClass = this.#validator.isString('toggleOpenClass', toggleOpenClass);
		}

		this.showDialogParameter = this.#validator.isString('showDialogParameter', showDialogParameter);

		this.#validator.isBoolean('dialogScrollEnabled', dialogScrollEnabled);

		this.init(dialogScrollEnabled === false ? false : this.#validator.isObject('dialogScrollIntoView', dialogScrollIntoView));
	}

	/**
	 * Initialize default class methods and functionality.
	 *
	 * @param {Object} dialogScrollIntoView The scroll into view options object.
	 */
	init (dialogScrollIntoView) {
		this.bindEvents();

		// Open and scroll to dialog element after successful login or account creation
		if (new URLSearchParams(window.location.search).has(this.showDialogParameter)) {
			this.controlElement.click();

			if (typeof dialogScrollIntoView === 'object') {
				this.dialogElement.scrollIntoView(dialogScrollIntoView);
			}
		}
	}

	/**
	 * Bind element events.
	 */
	bindEvents () {
		this.bindControlElementEvents();

		if (this.toggle.enabled) {
			this.bindToggleEvents();
		}
	}

	/**
	 * Bind the control element events.
	 */
	bindControlElementEvents () {
		this.controlElement.addEventListener('click', (event) => {
			event.preventDefault();

			if (this.controlElement.getAttribute('aria-expanded') === 'true') {
				MicroModal.close(this.dialogElement.id);
			} else {
				MicroModal.show(this.dialogElement.id, {
					onClose: () => {
						this.controlElement.setAttribute('aria-expanded', 'false');
					},
					onShow: () => {
						this.controlElement.setAttribute('aria-expanded', 'true');
					}
				});
			}
		});

		if (!this.dialogElement.classList.contains('has-overlay')) {
			document.addEventListener('mousedown', (event) => {
				event.stopPropagation();

				if (this.controlElement.getAttribute('aria-expanded') === 'false') {
					return;
				}

				if (this.controlElement === event.target || this.controlElement.contains(event.target) || this.dialogContainerElement === event.target || this.dialogContainerElement.contains(event.target)) {
					return;
				}

				this.controlElement.click();
			});
		}
	}

	/**
	 * Bind the toggle functionality events.
	 */
	bindToggleEvents () {
		this.toggle.elements.primary.controlElement.addEventListener('click', (event) => {
			event.preventDefault();

			this.toggleElements(this.toggle.elements.primary, this.toggle.elements.secondary);
		});

		this.toggle.elements.secondary.controlElement.addEventListener('click', (event) => {
			event.preventDefault();

			this.toggleElements(this.toggle.elements.secondary, this.toggle.elements.primary);
		});
	}

	/**
	 * Toggle elements to show and hide.
	 *
	 * @param {Object} showToggleElements The toggle options for the elements to show.
	 * @param {Object} hideToggleElements The toggle options for the elements to hide.
	 */
	toggleElements (showToggleElements, hideToggleElements) {
		hideToggleElements.element.classList.remove(this.toggle.openClass);
		hideToggleElements.controlElement.setAttribute('aria-expanded', 'false');
		hideToggleElements.controlElement.classList.remove('u-hidden');
		hideToggleElements.controlElement.focus();

		showToggleElements.controlElement.classList.add('u-hidden');
		showToggleElements.controlElement.setAttribute('aria-expanded', 'true');
		showToggleElements.element.classList.add(this.toggle.openClass);
	}
}
